/**
 * Wasserij Store — Pipeline, inkomend volume, productie data
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';

// Types
export interface PipelineStatus {
  status: string;
  itemCount: number;
  totalM2: number;
}

export interface InkomendDag {
  datum: string;
  aantalOrders: number;
  aantalItems: number;
  totalM2: number;
}

export interface ProductieDag {
  datum: string;
  itemCount: number;
  totalM2: number;
}

export interface WasserijItem {
  id: string;
  name: string;
  item: string;
  oppervlakteM2: number;
  lengte: number;
  breedte: number;
  werkOrderName: string;
  klantNaam: string;
  fotoUrl: string | null;
  extraBehandelingen: string | null;
  sfUrl: string;
  sfWerkOrderUrl: string | null;
  ophaalDatum: string;
  createdDate: string;
}

/** Vastgelopen kleed — spiegelt de respons van GET /api/wasserij/stilstand. */
export interface StilstandItem {
  id: string;
  ref: string;
  itemStatus: string;
  woStatus: string;
  woRef: string | null;
  woId: string | null;
  klantNaam: string | null;
  oppervlakteM2: number;
  daysStuck: number;
  lastActivity: string;
  lastHandling: string | null;
  drempelDagen: number;
  broersVerder: number;
  afleverritGepland: boolean;
}

interface WasserijState {
  // Data
  pipeline: PipelineStatus[];
  inkomend: InkomendDag[];
  productie: ProductieDag[];
  statusDetail: WasserijItem[];
  selectedStatus: string | null;
  stilstand: StilstandItem[];
  stilstandStale: boolean;
  stilstandSyncedAt: string | null;

  // Loading
  isLoadingPipeline: boolean;
  isLoadingInkomend: boolean;
  isLoadingProductie: boolean;
  isLoadingDetail: boolean;
  isLoadingStilstand: boolean;

  // Totals (derived)
  pipelineTotal: { items: number; m2: number };
  inkomendTotal7d: { orders: number; items: number; m2: number };
  productieAvg14d: { items: number; m2: number };

  // Polling
  pollingIntervalId: ReturnType<typeof setInterval> | null;

  // Actions
  fetchPipeline: () => Promise<void>;
  fetchInkomend: (days?: number) => Promise<void>;
  fetchProductie: (from?: string, to?: string) => Promise<void>;
  fetchStilstand: () => Promise<void>;
  fetchStatusDetail: (status: string) => Promise<void>;
  setSelectedStatus: (status: string | null) => void;
  fetchAll: () => Promise<void>;
  startPolling: (intervalMs?: number) => void;
  stopPolling: () => void;
}

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

/** Directe GET op een office-app /api endpoint (mirror-gedreven, geen sf-proxy).
 *  B5 27-08: de vier overzicht-views gingen hiervoor als laatste levende
 *  proxy-lezer via /api/sf-proxy naar InternalWasserijRest. */
async function apiFetch(path: string): Promise<unknown> {
  const token = await getToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(path, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  return response.json();
}

export const useWasserijStore = create<WasserijState>()((set, get) => ({
  pipeline: [],
  inkomend: [],
  productie: [],
  statusDetail: [],
  selectedStatus: null,
  stilstand: [],
  stilstandStale: false,
  stilstandSyncedAt: null,
  isLoadingPipeline: false,
  isLoadingInkomend: false,
  isLoadingProductie: false,
  isLoadingDetail: false,
  isLoadingStilstand: false,
  pipelineTotal: { items: 0, m2: 0 },
  inkomendTotal7d: { orders: 0, items: 0, m2: 0 },
  productieAvg14d: { items: 0, m2: 0 },
  pollingIntervalId: null,

  fetchPipeline: async () => {
    set({ isLoadingPipeline: true });
    try {
      const data = await apiFetch('/api/wasserij/overzicht?view=pipeline') as PipelineStatus[];
      const total = data.reduce(
        (acc, s) => ({ items: acc.items + s.itemCount, m2: acc.m2 + (s.totalM2 || 0) }),
        { items: 0, m2: 0 }
      );
      set({ pipeline: data, pipelineTotal: total, isLoadingPipeline: false });
    } catch (error) {
      console.error('[Wasserij] fetchPipeline error:', error);
      set({ isLoadingPipeline: false });
    }
  },

  fetchInkomend: async (days = 14) => {
    set({ isLoadingInkomend: true });
    try {
      const data = await apiFetch(`/api/wasserij/overzicht?view=inkomend&days=${days}`) as { dagen: InkomendDag[] };
      const dagen = data.dagen || [];
      // Calculate 7-day total
      const sevenDays = dagen.slice(0, 7);
      const total = sevenDays.reduce(
        (acc, d) => ({
          orders: acc.orders + d.aantalOrders,
          items: acc.items + d.aantalItems,
          m2: acc.m2 + (d.totalM2 || 0),
        }),
        { orders: 0, items: 0, m2: 0 }
      );
      set({ inkomend: dagen, inkomendTotal7d: total, isLoadingInkomend: false });
    } catch (error) {
      console.error('[Wasserij] fetchInkomend error:', error);
      set({ isLoadingInkomend: false });
    }
  },

  fetchProductie: async (from, to) => {
    set({ isLoadingProductie: true });
    try {
      let url = '/api/wasserij/overzicht?view=productie';
      if (from) url += `&from=${from}`;
      if (to) url += `&to=${to}`;
      const data = await apiFetch(url) as ProductieDag[];
      // Calculate 14-day average
      const count = data.length || 1;
      const totals = data.reduce(
        (acc, d) => ({ items: acc.items + d.itemCount, m2: acc.m2 + (d.totalM2 || 0) }),
        { items: 0, m2: 0 }
      );
      set({
        productie: data,
        productieAvg14d: { items: Math.round(totals.items / count), m2: Math.round(totals.m2 / count) },
        isLoadingProductie: false,
      });
    } catch (error) {
      console.error('[Wasserij] fetchProductie error:', error);
      set({ isLoadingProductie: false });
    }
  },

  fetchStilstand: async () => {
    set({ isLoadingStilstand: true });
    try {
      const data = await apiFetch('/api/wasserij/stilstand') as {
        items: StilstandItem[];
        stale?: boolean;
        syncedAt?: string | null;
      };
      set({
        stilstand: data.items || [],
        stilstandStale: data.stale ?? false,
        stilstandSyncedAt: data.syncedAt ?? null,
        isLoadingStilstand: false,
      });
    } catch (error) {
      console.error('[Wasserij] fetchStilstand error:', error);
      set({ isLoadingStilstand: false });
    }
  },

  fetchStatusDetail: async (status) => {
    set({ isLoadingDetail: true, selectedStatus: status });
    try {
      const data = await apiFetch(
        `/api/wasserij/overzicht?view=status&name=${encodeURIComponent(status)}`
      ) as WasserijItem[];
      set({ statusDetail: data, isLoadingDetail: false });
    } catch (error) {
      console.error('[Wasserij] fetchStatusDetail error:', error);
      set({ isLoadingDetail: false });
    }
  },

  setSelectedStatus: (status) => {
    if (status) {
      get().fetchStatusDetail(status);
    } else {
      set({ selectedStatus: null, statusDetail: [] });
    }
  },

  fetchAll: async () => {
    await Promise.all([
      get().fetchPipeline(),
      get().fetchInkomend(),
      get().fetchProductie(),
      get().fetchStilstand(),
    ]);
  },

  startPolling: (intervalMs = 300000) => {
    const { pollingIntervalId } = get();
    if (pollingIntervalId) return;

    get().fetchAll();

    const id = setInterval(() => {
      get().fetchAll();
    }, intervalMs);
    set({ pollingIntervalId: id });
  },

  stopPolling: () => {
    const { pollingIntervalId } = get();
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      set({ pollingIntervalId: null });
    }
  },
}));
